'use client';

import React, { ReactNode } from 'react';
import styles from './toast.module.sass';

export type Variant = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  variant: Variant;
  title: string;
  message?: string;
  onClose?: () => void;
  icon?: ReactNode;
  accentBar?: boolean;
}

const defaultIcons: Record<Variant, ReactNode> = {
  success: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  error: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  info: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
};

export const Toast: React.FC<ToastProps> = ({
  variant,
  title,
  message,
  onClose,
  icon,
  accentBar = false,
}) => {
  const iconToDisplay = icon !== undefined ? icon : defaultIcons[variant];
  const toastClasses = [
    styles.toast,
    styles[variant],
    accentBar && styles['accent-bar'],
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={toastClasses}>
      <div className={styles['toast-icon']}>{iconToDisplay}</div>
      <div className={styles['toast-content']}>
        <div className={styles['toast-title']}>{title}</div>
        {message && <div className={styles['toast-message']}>{message}</div>}
      </div>
      {onClose && (
        <button
          className={styles['toast-close']}
          onClick={onClose}
          aria-label="Close notification"
        >
          &times;
        </button>
      )}
    </div>
  );
};
